/* FLOAT Reviews — submission form client (multi-step) */
(function () {
  'use strict';

  // Always start at top of the page on (re)load — review form is a focused
  // single-task surface, browser auto-restored scroll feels misplaced.
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
  window.scrollTo(0, 0);

  const config = window.FRW_CONFIG || {};
  const params = new URLSearchParams(window.location.search);
  const token = params.get('t');

  const $ = (sel) => document.querySelector(sel);
  const els = {
    initialLoading: $('[data-frw-initial-loading]'),
    tokenError: $('[data-frw-token-error]'),
    tokenErrorMsg: $('[data-frw-token-error-msg]'),
    hero: $('[data-frw-hero]'),
    productImage: $('[data-frw-product-image]'),
    productTitle: $('[data-frw-product-title]'),
    greeting: $('[data-frw-greeting]'),
    form: $('[data-frw-form]'),
    saved: $('[data-frw-saved]'),
    restore: $('[data-frw-restore]'),
    restoreResume: $('[data-frw-restore-resume]'),
    restoreDiscard: $('[data-frw-restore-discard]'),
    submitting: $('[data-frw-submitting]'),
    success: $('[data-frw-success]'),
    successName: $('[data-frw-success-name]'),
    successProduct: $('[data-frw-success-product]'),
    successProductLink: $('[data-frw-success-product-link]'),
    submitError: $('[data-frw-submit-error]'),
    submitErrorMsg: $('[data-frw-submit-error-msg]'),
    already: $('[data-frw-already]'),
    confettiCanvas: $('[data-frw-confetti-canvas]'),
  };

  /* ── Draft persistence (localStorage) ──────────────── */
  const DRAFT_KEY = token ? `frw-draft-${token.slice(0, 32)}` : null;

  function loadDraft() {
    if (!DRAFT_KEY) return null;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  }

  function saveDraft(data) {
    if (!DRAFT_KEY) return;
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ ...data, savedAt: Date.now() }));
    } catch {}
  }

  function clearDraft() {
    if (!DRAFT_KEY) return;
    try { localStorage.removeItem(DRAFT_KEY); } catch {}
  }

  let lookupResult = null;

  /* ── Bootstrap ──────────────────────────────────────── */
  if (!token) {
    return showTokenError('Geen reviewlink gevonden. Open de link uit je e-mail opnieuw.');
  }

  init();

  async function init() {
    try {
      const res = await fetch(config.lookupEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        return showTokenError(translateError(json.error, json));
      }

      lookupResult = json;
      renderHero(json);
      els.initialLoading.hidden = true;
      if (json.existingReview) {
        showAlreadyReviewed(json.existingReview);
        trackEvent('opened');
      } else {
        els.form.hidden = false;
        setupForm();
      }
    } catch (err) {
      console.error('lookup failed', err);
      showTokenError('We konden je link niet verifiëren. Controleer je internet en probeer opnieuw.');
    }
  }

  function renderHero(data) {
    els.hero.hidden = false;
    els.productTitle.textContent = data.productTitle || 'je FLOAT';
    if (data.productImage) {
      els.productImage.src = data.productImage;
      els.productImage.alt = data.productTitle;
    } else {
      els.productImage.style.display = 'none';
    }
    if (data.customerFirstName) {
      els.greeting.textContent = `Hoi ${data.customerFirstName}, leuk dat je een review schrijft.`;
    } else {
      els.greeting.textContent = 'Leuk dat je een review schrijft.';
    }

    // Auto display name in step 3
    const authorEl = document.querySelector('[data-frw-author-display]');
    if (authorEl) authorEl.textContent = data.displayName || 'Geverifieerde koper';

    renderExamples(data.exampleReviews || []);
  }

  function renderExamples(reviews) {
    const container = $('[data-frw-examples]');
    const list = $('[data-frw-examples-list]');
    if (!container || !list || reviews.length === 0) return;

    list.innerHTML = '';
    reviews.forEach((r) => {
      const card = document.createElement('div');
      card.className = 'frw-example';
      const stars = '★'.repeat(Math.min(5, Math.max(1, r.rating))) + '☆'.repeat(5 - Math.min(5, Math.max(1, r.rating)));
      card.innerHTML = `
        <div class="frw-example-head">
          <span class="frw-example-stars" aria-label="${r.rating} sterren">${stars}</span>
          <span class="frw-example-author">${escapeHtml(r.author)}</span>
        </div>
        ${r.title ? `<p class="frw-example-title">${escapeHtml(r.title)}</p>` : ''}
        <p class="frw-example-body">${escapeHtml(r.body).slice(0, 200)}${r.body.length > 200 ? '…' : ''}</p>
      `;
      list.appendChild(card);
    });
    container.hidden = false;
  }

  function escapeHtml(s) {
    const div = document.createElement('div');
    div.textContent = String(s ?? '');
    return div.innerHTML;
  }

  function showTokenError(msg) {
    els.initialLoading.hidden = true;
    els.tokenErrorMsg.textContent = msg;
    els.tokenError.hidden = false;
  }

  /* ── Form setup ─────────────────────────────────────── */
  function setupForm() {
    setupStars();
    setupTitleSuggestions();
    setupCounter();
    setupPhotos();
    setupSubmit();
    setupAutosave();
    setupRestore();
    trackEvent('opened');
  }

  /* Autosave — debounced on every input change */
  let autosaveTimer = null;
  function setupAutosave() {
    els.form.addEventListener('input', queueAutosave);
    els.form.addEventListener('change', queueAutosave);
  }

  function queueAutosave() {
    clearTimeout(autosaveTimer);
    autosaveTimer = setTimeout(() => saveDraft(snapshotForm()), 600);
  }

  function snapshotForm() {
    const fd = new FormData(els.form);
    return {
      rating: fd.get('rating') || null,
      title: fd.get('title') || '',
      body: fd.get('body') || '',
    };
  }

  /* Restore from existing draft on page load */
  function setupRestore() {
    const draft = loadDraft();
    if (!draft || !els.restore) return;

    els.form.hidden = true;
    els.restore.hidden = false;

    if (els.restoreResume) {
      els.restoreResume.addEventListener('click', () => {
        applyDraft(draft);
        els.restore.hidden = true;
        els.form.hidden = false;
      });
    }
    if (els.restoreDiscard) {
      els.restoreDiscard.addEventListener('click', () => {
        clearDraft();
        els.restore.hidden = true;
        els.form.hidden = false;
      });
    }
  }

  function applyDraft(d) {
    if (d.rating) {
      const radio = els.form.querySelector(`input[name="rating"][value="${d.rating}"]`);
      if (radio) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change'));
      }
    }
    if (d.title) els.form.querySelector('input[name="title"]').value = d.title;
    if (d.body) {
      const body = els.form.querySelector('textarea[name="body"]');
      body.value = d.body;
      body.dispatchEvent(new Event('input'));
    }
  }

  /* Track lifecycle events to backend (for reminder email Flow) */
  async function trackEvent(event) {
    if (!config.lookupEndpoint) return;
    const url = config.lookupEndpoint.replace('/lookup-token', '/track-event');
    try {
      await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, event }),
        keepalive: true,
      });
    } catch {}
  }

  /* Rotating title placeholder suggestions — light, interactive nudge */
  function setupTitleSuggestions() {
    const input = els.form.querySelector('[data-frw-title-input]');
    if (!input) return;

    const suggestions = [
      'Slaapt heerlijk',
      'Beste aankoop dit jaar',
      'Verrassend comfortabel',
      'Perfect voor onze camper',
      'Logés sliepen top',
      'Binnen 3 minuten klaar',
      'Vlak en stevig',
      'Voelt als een echt bed',
      'Mooi product, snelle levering',
      'Festivals worden makkelijker',
    ];

    // Shuffle so each visit feels fresh
    const order = suggestions
      .map((s) => ({ s, r: Math.random() }))
      .sort((a, b) => a.r - b.r)
      .map((x) => x.s);

    let idx = 0;
    let timer = null;
    let paused = false;

    function next() {
      if (paused || input.value.length > 0) return;
      idx = (idx + 1) % order.length;
      input.style.transition = 'opacity .2s ease';
      input.style.opacity = '0.5';
      setTimeout(() => {
        input.placeholder = order[idx];
        input.style.opacity = '1';
      }, 200);
    }

    input.placeholder = order[0];
    timer = setInterval(next, 2400);

    input.addEventListener('focus', () => { paused = true; });
    input.addEventListener('blur', () => { paused = input.value.length > 0; });
    input.addEventListener('input', () => { paused = input.value.length > 0; });
  }

  /* Stars input — refined, no bounce, no emoji */
  function setupStars() {
    const group = els.form.querySelector('.frw-stars-input');
    const inputs = group.querySelectorAll('input[name="rating"]');
    const labels = group.querySelectorAll('.frw-star-label');
    const feedback = els.form.querySelector('[data-frw-rating-feedback]');
    const messages = ['', 'Niet tevreden', 'Kan beter', 'Naar verwachting', 'Goed', 'Uitstekend'];
    let selected = 0;

    function paint(n, isSelected) {
      group.className =
        'frw-stars-input' +
        (n > 0 ? ' frw-stars-input--filled-' + n : '') +
        (isSelected ? ' frw-stars-input--selected' : '');
    }

    labels.forEach((label, i) => {
      label.addEventListener('mouseenter', () => paint(i + 1, false));
      label.addEventListener('mouseleave', () => paint(selected, selected > 0));
    });
    inputs.forEach((input, i) => {
      input.addEventListener('change', () => {
        selected = i + 1;
        paint(selected, true);
        if (feedback) feedback.textContent = messages[selected];
      });
    });
  }

  /* Body counter */
  function setupCounter() {
    const body = els.form.querySelector('textarea[name="body"]');
    const counter = els.form.querySelector('[data-frw-counter]');
    body.addEventListener('input', () => {
      const len = body.value.length;
      counter.textContent = len < 20 ? `${len} / 20 minimum` : `${len} / 2000`;
      counter.classList.toggle('frw-counter--ok', len >= 20);
    });
  }

  /* Photo upload — auto-compresses on select (max 1600px wide JPEG) */
  const MAX_PHOTOS = 3;
  const MAX_DIMENSION = 1600;
  const JPEG_QUALITY = 0.85;
  let photoFiles = []; // Array of compressed File objects

  function setupPhotos() {
    const input = els.form.querySelector('input[name="photos"]');
    const thumbs = els.form.querySelector('[data-frw-photo-thumbs]');

    input.addEventListener('change', async () => {
      const selected = Array.from(input.files).slice(0, MAX_PHOTOS);
      if (selected.length === 0) return;

      // Show pending state
      thumbs.innerHTML = '<div class="frw-thumb-loading">Foto\'s verwerken…</div>';

      try {
        const compressed = await Promise.all(selected.map(compressImage));
        photoFiles = compressed;
        renderThumbs(thumbs);
      } catch (err) {
        console.error('compress failed', err);
        alert('Een foto kon niet verwerkt worden. Probeer een andere.');
        input.value = '';
        photoFiles = [];
        renderThumbs(thumbs);
      }
    });
  }

  async function compressImage(file) {
    if (!file.type.startsWith('image/')) throw new Error('not_image');

    const dataUrl = await readAsDataUrl(file);
    const img = await loadImage(dataUrl);

    let { width, height } = img;
    if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
      const scale = MAX_DIMENSION / Math.max(width, height);
      width = Math.round(width * scale);
      height = Math.round(height * scale);
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
    if (!blob) throw new Error('toblob_failed');

    // Wrap in a File so we keep filename + type for backend
    const baseName = file.name.replace(/\.[^.]+$/, '');
    return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
  }

  function loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  }

  function renderThumbs(container) {
    container.innerHTML = '';
    photoFiles.forEach((f, idx) => {
      const wrap = document.createElement('div');
      wrap.className = 'frw-thumb';
      const url = URL.createObjectURL(f);
      const img = document.createElement('img');
      img.src = url;
      wrap.appendChild(img);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'frw-thumb-remove';
      btn.textContent = '×';
      btn.setAttribute('aria-label', 'Verwijder foto');
      btn.addEventListener('click', () => {
        URL.revokeObjectURL(url);
        photoFiles.splice(idx, 1);
        renderThumbs(container);
      });
      wrap.appendChild(btn);
      container.appendChild(wrap);
    });
  }

  /* Submission */
  function setupSubmit() {
    els.form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!els.form.checkValidity()) {
        els.form.reportValidity();
        return;
      }
      const checked = els.form.querySelector('input[name="rating"]:checked');
      if (!checked) {
        alert('Kies eerst een aantal sterren.');
        return;
      }

      els.form.hidden = true;
      els.submitting.hidden = false;

      try {
        const photoPayload = await Promise.all(
          photoFiles.map(async (f) => ({
            filename: f.name,
            mimeType: f.type,
            sizeBytes: f.size,
            dataUrl: await readAsDataUrl(f),
          }))
        );

        const fd = new FormData(els.form);
        const body = {
          token,
          rating: Number(fd.get('rating')),
          title: fd.get('title') || undefined,
          body: fd.get('body'),
          // authorName niet meer in form — backend gebruikt order customer name
          photos: photoPayload,
        };

        const res = await fetch(config.submitEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const json = await res.json().catch(() => ({}));

        if (res.status === 409 && json.error === 'already_reviewed') {
          clearDraft();
          showAlreadyReviewed(json.existingReview || { rating: body.rating, title: body.title, body: body.body, authorName: '', datePublisher: null, moderationStatus: 'pending' });
          return;
        }
        if (!res.ok) throw new Error(translateError(json.error, json));

        showSuccess();
      } catch (err) {
        console.error(err);
        showSubmitError(err.message || 'Er ging iets mis. Probeer het later opnieuw.');
      }
    });

    const retry = $('[data-frw-retry]');
    if (retry) retry.addEventListener('click', () => {
      els.submitError.hidden = true;
      els.form.hidden = false;
    });
  }

  function showSuccess() {
    clearDraft();
    trackEvent('completed');
    els.submitting.hidden = true;
    els.success.hidden = false;
    const name = lookupResult && lookupResult.customerFirstName;
    if (els.successName) els.successName.textContent = name || '';
    renderPreview();
    if (lookupResult) {
      if (els.successProduct) els.successProduct.textContent = lookupResult.productTitle;
      if (lookupResult.productHandle && els.successProductLink) {
        els.successProductLink.href = `/products/${lookupResult.productHandle}`;
        els.successProductLink.hidden = false;
      }
    }
    fireConfetti();
  }

  function showSubmitError(msg) {
    els.submitting.hidden = true;
    els.submitErrorMsg.textContent = msg;
    els.submitError.hidden = false;
  }

  function showAlreadyReviewed(review) {
    els.form.hidden = true;
    els.submitting.hidden = true;
    els.already.hidden = false;

    const author = (lookupResult && lookupResult.displayName) || review.authorName || 'Geverifieerde koper';
    const initials = author.split(/\s+/).map((w) => w.charAt(0).toUpperCase()).filter((c) => /[A-Z]/.test(c)).slice(0, 2).join('');

    const set = (sel, fn) => { const el = $(sel); if (el) fn(el); };

    set('[data-frw-already-avatar]', (el) => { el.textContent = initials || '?'; });
    set('[data-frw-already-author]', (el) => { el.textContent = author; });
    set('[data-frw-already-stars]', (el) => {
      el.innerHTML = '';
      const r = Math.max(1, Math.min(5, Number(review.rating) || 5));
      for (let i = 1; i <= 5; i++) {
        const s = document.createElement('span');
        s.textContent = '★';
        s.className = i <= r ? 'frw-preview-star is-on' : 'frw-preview-star';
        el.appendChild(s);
      }
    });
    set('[data-frw-already-title]', (el) => {
      if (review.title) { el.textContent = review.title; el.hidden = false; }
    });
    set('[data-frw-already-body]', (el) => { el.textContent = review.body || ''; });
    set('[data-frw-already-date]', (el) => {
      if (review.datePublisher) {
        const d = new Date(review.datePublisher);
        el.textContent = d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
        el.dateTime = d.toISOString().slice(0, 10);
      }
    });
    set('[data-frw-already-status]', (el) => {
      const s = (review.moderationStatus || '').toLowerCase();
      if (s === 'approved') { el.textContent = 'Gepubliceerd'; el.className = 'frw-preview-verified'; }
      else if (s === 'rejected') { el.textContent = 'Niet goedgekeurd'; el.style.color = '#b91c1c'; }
      else { el.textContent = 'Wacht op goedkeuring'; el.style.color = '#b45309'; }
    });

    if (lookupResult && lookupResult.productHandle) {
      const link = $('[data-frw-already-product-link]');
      if (link) { link.href = `/products/${lookupResult.productHandle}`; link.hidden = false; }
    }
  }

  function renderPreview() {
    const fd = new FormData(els.form);
    const rating = Math.max(1, Math.min(5, Number(fd.get('rating')) || 5));
    const title = (fd.get('title') || '').trim();
    const body = (fd.get('body') || '').trim();
    const author = (lookupResult && lookupResult.displayName) || 'Geverifieerde koper';

    const avatarEl = $('[data-frw-preview-avatar]');
    if (avatarEl) {
      const initials = author
        .split(/\s+/)
        .map((w) => w.charAt(0).toUpperCase())
        .filter((c) => /[A-Z]/.test(c))
        .slice(0, 2)
        .join('');
      avatarEl.textContent = initials || '?';
    }

    const starsEl = $('[data-frw-preview-stars]');
    if (starsEl) {
      starsEl.innerHTML = '';
      for (let i = 1; i <= 5; i++) {
        const s = document.createElement('span');
        s.textContent = '★';
        s.className = i <= rating ? 'frw-preview-star is-on' : 'frw-preview-star';
        starsEl.appendChild(s);
      }
    }

    const titleEl = $('[data-frw-preview-title]');
    if (titleEl) {
      if (title) {
        titleEl.textContent = title;
        titleEl.hidden = false;
      } else {
        titleEl.hidden = true;
      }
    }

    const bodyEl = $('[data-frw-preview-body]');
    if (bodyEl) bodyEl.textContent = body;

    const authorEl = $('[data-frw-preview-author]');
    if (authorEl) authorEl.textContent = author;

    const dateEl = $('[data-frw-preview-date]');
    if (dateEl) {
      const today = new Date();
      const fmt = today.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
      dateEl.textContent = fmt;
      dateEl.dateTime = today.toISOString().slice(0, 10);
    }

    const photosEl = $('[data-frw-preview-photos]');
    if (photosEl) {
      photosEl.innerHTML = '';
      if (photoFiles.length > 0) {
        photoFiles.forEach((f) => {
          const img = document.createElement('img');
          img.src = URL.createObjectURL(f);
          img.alt = '';
          img.className = 'frw-preview-photo';
          photosEl.appendChild(img);
        });
        photosEl.hidden = false;
      } else {
        photosEl.hidden = true;
      }
    }
  }

  /* Confetti */
  function fireConfetti() {
    if (typeof window.confetti !== 'function') return;
    const fire = (particleRatio, opts) => {
      window.confetti({
        origin: { y: 0.6 },
        ...opts,
        particleCount: Math.floor(200 * particleRatio),
      });
    };
    fire(0.25, { spread: 26, startVelocity: 55 });
    fire(0.2, { spread: 60 });
    fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
    fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
    fire(0.1, { spread: 120, startVelocity: 45 });
  }

  /* Helpers */
  function readAsDataUrl(file) {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(file);
    });
  }

  function translateError(code, ctx) {
    const map = {
      missing_token: 'Geen geldige link.',
      invalid_token: 'Deze link is niet geldig of verlopen (90 dagen geldig na ontvangst).',
      order_not_found: 'We kunnen je bestelling niet meer terugvinden. Neem contact op via /pages/contact.',
      product_not_in_order: 'Dit product zit niet (meer) in deze bestelling.',
      already_reviewed: 'Je hebt dit product al gereviewd. Bedankt!',
      invalid_rating: 'Kies een aantal sterren.',
      body_too_short: `Je review is te kort (minimaal ${ctx.min} tekens).`,
      body_too_long: 'Je review is te lang (max 2000 tekens).',
      missing_author_name: 'Vul je naam in.',
      invalid_email: 'Vul een geldig e-mailadres in.',
      too_many_photos: 'Maximaal 3 foto\'s.',
      photo_too_large: 'Een foto is te groot (max 800KB).',
      photo_not_image: 'Een geüpload bestand is geen foto.',
      video_not_supported_yet: 'Video upload komt later beschikbaar.',
      internal: 'Server fout. Probeer het later opnieuw.',
    };
    return map[code] || 'Er ging iets mis.';
  }
})();
